import { resolveCssVariable } from '@/utils/cssUtility';
import { Avatar } from 'primereact/avatar';
import { Card } from 'primereact/card';
import { Chart } from 'primereact/chart';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Skeleton } from 'primereact/skeleton';
import { Tag } from 'primereact/tag';

interface SampleData {
  id: number;
  name: string;
  email: string;
  status: 'active' | 'inactive' | 'pending';
  role: string;
}

export const DataDisplaySection = () => {
  const sampleData: SampleData[] = [
    {
      id: 1,
      name: 'John Doe',
      email: 'john@example.com',
      status: 'active',
      role: 'Admin',
    },
    {
      id: 2,
      name: 'Jane Smith',
      email: 'jane@example.com',
      status: 'active',
      role: 'User',
    },
    {
      id: 3,
      name: 'Bob Johnson',
      email: 'bob@example.com',
      status: 'inactive',
      role: 'User',
    },
    {
      id: 4,
      name: 'Alice Brown',
      email: 'alice@example.com',
      status: 'pending',
      role: 'Moderator',
    },
  ];

  const statusBodyTemplate = (rowData: SampleData) => {
    const severityMap: Record<string, 'success' | 'danger' | 'warning'> = {
      active: 'success',
      inactive: 'danger',
      pending: 'warning',
    };
    return (
      <Tag value={rowData.status} severity={severityMap[rowData.status]} />
    );
  };

  const chartData = {
    labels: ['January', 'February', 'March', 'April', 'May'],
    datasets: [
      {
        label: 'Responses',
        data: [65, 59, 80, 81, 56],
        backgroundColor: 'rgba(54, 162, 235, 0.2)',
        borderColor: 'rgb(54, 162, 235)',
        borderWidth: 2,
      },
    ],
  };

  const chartOptions = {
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
      },
    },
  };

  const pieData = {
    labels: ['Completed', 'In Progress', 'Not Started'],
    datasets: [
      {
        data: [300, 150, 100],
        backgroundColor: [
          'rgb(34, 197, 94)',
          'rgb(59, 130, 246)',
          'rgb(156, 163, 175)',
        ],
      },
    ],
  };

  return (
    <div id='data-display' className='space-y-6'>
      <div>
        <h2 className='text-2xl font-bold mb-2'>Data Display</h2>
        <p className='text-gray-600 mb-4'>
          Components for displaying data, tables, and visualizations.
        </p>
      </div>

      {/* Card */}
      <Card title='Card Component' className='mb-4'>
        <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
          <Card title='Basic Card'>
            <p className='text-gray-600'>
              This is a basic card with a title and content area.
            </p>
          </Card>

          <Card title='Card with Subtitle' subTitle='Supporting text'>
            <p className='text-gray-600'>
              Cards can have subtitles for additional context.
            </p>
          </Card>

          <Card
            title='Card with Footer'
            footer={
              <div className='flex gap-2'>
                <button className='px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded'>
                  Cancel
                </button>
                <button className='px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700'>
                  Save
                </button>
              </div>
            }
          >
            <p className='text-gray-600'>
              This card includes a footer with action buttons.
            </p>
          </Card>
        </div>
      </Card>

      {/* DataTable */}
      <Card title='DataTable' className='mb-4'>
        <DataTable value={sampleData} paginator rows={5} className='w-full'>
          <Column field='id' header='ID' sortable style={{ width: '10%' }} />
          <Column field='name' header='Name' sortable />
          <Column field='email' header='Email' />
          <Column field='role' header='Role' sortable />
          <Column header='Status' body={statusBodyTemplate} />
        </DataTable>
      </Card>

      {/* Tag */}
      <Card title='Tag' className='mb-4'>
        <div className='space-y-4'>
          <div>
            <h3 className='text-sm font-semibold mb-3 text-gray-700'>
              Status Tags
            </h3>
            <div className='flex flex-wrap gap-3'>
              <Tag value='Success' severity='success' />
              <Tag value='Info' severity='info' />
              <Tag value='Warning' severity='warning' />
              <Tag value='Danger' severity='danger' />
              <Tag value='Secondary' />
            </div>
          </div>

          <div>
            <h3 className='text-sm font-semibold mb-3 text-gray-700'>
              Survey Status Examples
            </h3>
            <div className='flex flex-wrap gap-3'>
              <Tag value='Published' severity='success' />
              <Tag value='Draft' severity='info' />
              <Tag value='Scheduled' severity='warning' />
              <Tag value='Completed' severity='danger' />
              <Tag value='Archived' />
            </div>
          </div>

          <div>
            <h3 className='text-sm font-semibold mb-3 text-gray-700'>
              Rounded Tags
            </h3>
            <div className='flex flex-wrap gap-3'>
              <Tag value='Active' severity='success' rounded />
              <Tag value='Pending' severity='warning' rounded />
              <Tag value='Inactive' severity='danger' rounded />
            </div>
          </div>
        </div>
      </Card>

      {/* Avatar */}
      <Card title='Avatar' className='mb-4'>
        <div className='space-y-4'>
          <div>
            <h3 className='text-sm font-semibold mb-3 text-gray-700'>Sizes</h3>
            <div className='flex items-center gap-4'>
              <Avatar label='SM' size='normal' shape='circle' />
              <Avatar label='MD' size='large' shape='circle' />
              <Avatar label='LG' size='xlarge' shape='circle' />
            </div>
          </div>

          <div>
            <h3 className='text-sm font-semibold mb-3 text-gray-700'>Shapes</h3>
            <div className='flex items-center gap-4'>
              <Avatar label='JD' shape='circle' />
              <Avatar label='AB' shape='square' />
            </div>
          </div>

          <div>
            <h3 className='text-sm font-semibold mb-3 text-gray-700'>
              With Icons
            </h3>
            <div className='flex items-center gap-4'>
              <Avatar icon='pi pi-user' shape='circle' />
              <Avatar
                icon='pi pi-users'
                shape='circle'
                style={{
                  backgroundColor: resolveCssVariable('--color-russian-blue'),
                  color: resolveCssVariable('--color-white'),
                }}
              />
              <Avatar
                icon='pi pi-cog'
                shape='circle'
                style={{
                  backgroundColor: resolveCssVariable('--color-rich-magenta'),
                  color: resolveCssVariable('--color-white'),
                }}
              />
            </div>
          </div>
        </div>
      </Card>

      {/* Skeleton */}
      <Card title='Skeleton (Loading States)' className='mb-4'>
        <div className='space-y-4'>
          <div>
            <h3 className='text-sm font-semibold mb-3 text-gray-700'>
              Text Skeleton
            </h3>
            <div className='space-y-2'>
              <Skeleton width='100%' height='1rem' />
              <Skeleton width='80%' height='1rem' />
              <Skeleton width='60%' height='1rem' />
            </div>
          </div>

          <div>
            <h3 className='text-sm font-semibold mb-3 text-gray-700'>
              Card Skeleton
            </h3>
            <div className='border rounded-lg p-4 space-y-3'>
              <Skeleton width='40%' height='1.5rem' />
              <Skeleton width='100%' height='4rem' />
              <div className='flex gap-2'>
                <Skeleton width='5rem' height='2rem' />
                <Skeleton width='5rem' height='2rem' />
              </div>
            </div>
          </div>

          <div>
            <h3 className='text-sm font-semibold mb-3 text-gray-700'>Shapes</h3>
            <div className='flex gap-4'>
              <Skeleton shape='circle' size='4rem' />
              <Skeleton width='10rem' height='4rem' />
            </div>
          </div>
        </div>
      </Card>

      {/* Charts */}
      <Card title='Charts' className='mb-4'>
        <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
          <div>
            <h3 className='text-sm font-semibold mb-3 text-gray-700'>
              Bar Chart
            </h3>
            <Chart
              type='bar'
              data={chartData}
              options={chartOptions}
              height='200px'
            />
          </div>
          <div>
            <h3 className='text-sm font-semibold mb-3 text-gray-700'>
              Line Chart
            </h3>
            <Chart
              type='line'
              data={chartData}
              options={chartOptions}
              height='200px'
            />
          </div>
          <div>
            <h3 className='text-sm font-semibold mb-3 text-gray-700'>
              Pie Chart
            </h3>
            <Chart type='pie' data={pieData} height='200px' />
          </div>
          <div>
            <h3 className='text-sm font-semibold mb-3 text-gray-700'>
              Doughnut Chart
            </h3>
            <Chart type='doughnut' data={pieData} height='200px' />
          </div>
        </div>
      </Card>
    </div>
  );
};
